// ─────────────────────────────────────────────────────────────────
// Glass-i — Investments pure-JS helpers (Stage 6B.9E).
//
// No React, no axios, no JSX so node:test can import these directly.
// The JSX components re-export the constants + use the helpers
// verbatim.
// ─────────────────────────────────────────────────────────────────

// camelCase grid field → snake_case backend whitelist for the
// server-mode grid. Mirrors the backend's
// INVESTMENTS_GRID_ALLOWED_FIELDS keys.
export const GRID_FIELD_TO_SQL = Object.freeze({
  investmentCode:              'investment_code',
  investmentName:              'investment_name',
  categoryId:                  'category_id',
  investmentLedgerAccountId:   'investment_ledger_account_id',
  institutionName:             'institution_name',
  investmentDate:              'investment_date',
  maturityDate:                'maturity_date',
  principalAmount:             'principal_amount',
  currentValue:                'current_value',
  accruedIncome:               'accrued_income',
  status:                      'status',
  disposalDate:                'disposal_date',
  createdAt:                   'created_at',
  updatedAt:                   'updated_at'
});

export function mapField(c) {
  return c && typeof c === 'string' ? (GRID_FIELD_TO_SQL[c] || c) : c;
}

function mapRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: mapRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

export function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters.filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort.filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) out.groupBy = mapField(out.groupBy);
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: mapRules(out.advancedFilters.rules)
    };
  }
  return out;
}

// ── Constants surfaced to the modal ──────────────────────────────
export const INVESTMENT_CLASSES = Object.freeze([
  { value: 'debt',    label: 'Debt' },
  { value: 'equity',  label: 'Equity' },
  { value: 'hybrid',  label: 'Hybrid' },
  { value: 'deposit', label: 'Deposit (FD/RD)' },
  { value: 'bond',    label: 'Bond / Govt. Securities' },
  { value: 'other',   label: 'Other' }
]);
export const INVESTMENT_STATUSES = Object.freeze([
  { value: 'active',   label: 'Active',   color: 'green' },
  { value: 'matured',  label: 'Matured',  color: 'blue' },
  { value: 'sold',     label: 'Sold',     color: 'gold' },
  { value: 'closed',   label: 'Closed',   color: 'default' }
]);
export const STATUS_COLOR = Object.freeze({
  active: 'green', matured: 'blue', sold: 'gold', closed: 'default'
});
export const STATUS_LABEL = Object.freeze({
  active: 'Active', matured: 'Matured', sold: 'Sold', closed: 'Closed'
});

export const INCOME_MODES = Object.freeze([
  { value: 'income_received', label: 'Income Received (cash/bank)' },
  { value: 'income_accrual',  label: 'Income Accrued (not yet received)' }
]);

export const DISPOSAL_STATUSES = Object.freeze([
  { value: 'sold',   label: 'Sold' },
  { value: 'closed', label: 'Closed' }
]);

// ── Pure helpers ─────────────────────────────────────────────────
function toNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function round2(n) {
  return Math.round((toNumber(n) + Number.EPSILON) * 100) / 100;
}

// Compute the gain/loss on disposal preview from current investment state.
//   gain = proceeds - principal   (positive = gain, negative = loss)
export function calculateGainLossPreview({
  principalAmount, disposalValue
}) {
  const principal = round2(principalAmount);
  const proceeds  = round2(disposalValue);
  const gainLoss = round2(proceeds - principal);
  return {
    principal, proceeds, gainLossOnDisposal: gainLoss,
    isGain: gainLoss > 0, isLoss: gainLoss < 0
  };
}

// INR thousand-separated 2dp formatter.
export function formatRupee(n) {
  const v = toNumber(n);
  return v.toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

// Client-side validation mirroring the backend Joi schema. Returns an
// array of friendly messages (empty when the form is valid).
export function findInvestmentFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.investmentName)              errs.push('Investment name is required.');
  if (!form.investmentLedgerAccountId)   errs.push('Select an Investment ledger.');
  if (!form.investmentDate)              errs.push('Investment date is required.');
  if (form.investmentDate && form.maturityDate) {
    const inv = new Date(form.investmentDate).getTime();
    const mat = new Date(form.maturityDate).getTime();
    if (Number.isFinite(inv) && Number.isFinite(mat) && mat < inv) {
      errs.push('Maturity date cannot be before investment date.');
    }
  }
  if (toNumber(form.principalAmount) < 0)  errs.push('Principal amount must be zero or greater.');
  if (toNumber(form.currentValue) < 0)     errs.push('Current value must be zero or greater.');
  if (form.units != null && form.units !== '' && toNumber(form.units) < 0) {
    errs.push('Units must be zero or greater.');
  }
  if (form.interestRate != null && form.interestRate !== ''
      && toNumber(form.interestRate) < 0) {
    errs.push('Interest rate must be zero or greater.');
  }
  return errs;
}

export function findIncomeFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.txnDate) errs.push('Transaction date is required.');
  if (!form.mode)    errs.push('Income mode is required.');
  if (!(toNumber(form.amount) > 0)) {
    errs.push('Income amount must be greater than zero.');
  }
  if (form.mode === 'income_received' && !form.receivingAccountId) {
    errs.push('A receiving account is required when recording income received.');
  }
  return errs;
}

export function findDisposalFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.disposalDate) errs.push('Disposal date is required.');
  if (toNumber(form.disposalValue) < 0) errs.push('Disposal value must be zero or greater.');
  if (toNumber(form.disposalValue) > 0 && !form.receivingAccountId) {
    errs.push('A receiving account is required when the disposal value is greater than zero.');
  }
  if (form.status && !['sold', 'closed'].includes(form.status)) {
    errs.push('Disposal status must be sold or closed.');
  }
  return errs;
}

export function findMaturityFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.maturityDate) errs.push('Maturity date is required.');
  if (toNumber(form.maturityValue) < 0) errs.push('Maturity value must be zero or greater.');
  if (toNumber(form.maturityValue) > 0 && !form.receivingAccountId) {
    errs.push('A receiving account is required when the maturity value is greater than zero.');
  }
  return errs;
}

export function findValuationFormErrors(form) {
  const errs = [];
  if (!form) return ['Form values are required.'];
  if (!form.valuationDate) errs.push('Valuation date is required.');
  if (toNumber(form.newCurrentValue) < 0) errs.push('New current value must be zero or greater.');
  return errs;
}

// Translate a caught error into a friendly string. Reuses the
// accounting error envelope: `error.response.data.error.{code,message}`.
const INV_CODE_OVERRIDES = Object.freeze({
  INV_NOT_FOUND:                       'Investment not found.',
  INV_CATEGORY_NOT_FOUND:              'Investment category not found.',
  INV_CATEGORY_IN_USE:                 null,
  INV_CODE_DUPLICATE:                  null,
  INV_CATEGORY_CODE_DUPLICATE:         null,
  INV_LEDGER_REQUIRED:                 null,
  INV_LEDGER_NOT_FOUND:                'Selected ledger is missing or has been deleted.',
  INV_LEDGER_INACTIVE:                 'Selected ledger is inactive.',
  INV_LEDGER_WRONG_TYPE:               null,
  INV_LEDGER_NOT_INVESTMENT:           'Investment ledger must be an active Investment ledger.',
  INV_INCOME_LEDGER_REQUIRED:          'Set an Income ledger on the investment before posting income.',
  INV_INCOME_LEDGER_WRONG_TYPE:        'Income ledger must be an active Income ledger.',
  INV_GAIN_LOSS_LEDGER_REQUIRED:       'Set a Gain/Loss ledger on the investment before disposing/maturing.',
  INV_GAIN_LOSS_LEDGER_WRONG_TYPE:     'Gain/Loss ledger must be an active Income or Expense ledger.',
  INV_NOT_DELETABLE:                   null,
  INV_HAS_INCOME:                      'Investment has accrued income. Close it instead of deleting.',
  INV_HAS_VOUCHERS:                    'Investment has linked accounting vouchers. Reverse them before deleting.',
  INV_HAS_TXNS:                        'Investment has posted transactions. Close it instead of deleting.',
  INV_NOT_DISPOSABLE:                  'Investment is already closed and cannot be disposed again.',
  INV_NOT_MATURABLE:                   'Investment is already closed and cannot be matured again.',
  INV_NOT_INCOMEABLE:                  'Investment is closed; only active investments can record income.',
  INV_NOT_VALUABLE:                    'Investment is closed; only active investments can be revalued.',
  INV_INCOME_AMOUNT_ZERO:              'Income amount must be greater than zero.',
  INV_DISPOSAL_UNBALANCED:             'Disposal voucher is not balanced. Please refresh the investment and try again.',
  INV_DISPOSAL_DEGENERATE:             'Disposal voucher would have no meaningful lines. Set principal/disposal value sensibly and retry.',
  INV_SEQUENCE_MISSING:                'Investment code sequence is not configured. Please create document sequences in Accounts Setup.',
  INV_RACE_LOST:                       'Investment changed concurrently. Refresh and retry.',
  INV_NOT_EDITABLE:                    null
});

export function friendlyErrorFor(err) {
  if (!err) return 'Something went wrong.';
  const body = err && err.response && err.response.data && err.response.data.error
    ? err.response.data.error : null;
  const code = body && body.code;
  if (code && Object.prototype.hasOwnProperty.call(INV_CODE_OVERRIDES, code)
      && INV_CODE_OVERRIDES[code]) {
    return INV_CODE_OVERRIDES[code];
  }
  return (body && body.message)
    || err.message
    || 'Request failed.';
}
